import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TrendingUp, CheckCircle2, Clock, AlertTriangle, Landmark } from "lucide-react";
import { getDashboard } from "./disbursementReportsApi";
import { bankApiError } from "./bankErrorUtils";

const formatMoney = (v) =>
  `₹ ${Number(v ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

function StatCard({ Icon, label, value, sublabel, tone = "blue" }) {
  const tones = {
    blue: { bg: "#eff6ff", fg: "#1e40af" },
    green: { bg: "#dcfce7", fg: "#166534" },
    amber: { bg: "#fef3c7", fg: "#854d0e" },
    red: { bg: "#fee2e2", fg: "#991b1b" },
    slate: { bg: "#f1f5f9", fg: "#0f172a" },
  };
  const t = tones[tone] || tones.blue;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 16,
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: t.bg,
          color: t.fg,
          width: 34,
          height: 34,
          borderRadius: 8,
        }}
      >
        <Icon size={16} />
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 2 }}>{value}</div>
      {sublabel && (
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{sublabel}</div>
      )}
    </div>
  );
}

/**
 * Props:
 *   payPeriodId — optional; when omitted, aggregates across all pay periods.
 */
export default function DisbursementDashboard({ payPeriodId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await getDashboard(payPeriodId));
    } catch (e) {
      setError(bankApiError(e) || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [payPeriodId]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = data?.totals || {};
  const bankRows = useMemo(() => data?.bank_distribution || [], [data]);
  const trend = useMemo(() => data?.payment_trend_6mo || [], [data]);
  const maxTrendAmount = useMemo(
    () =>
      Math.max(
        ...trend.map((t) => Number(t.total_amount || 0)),
        1
      ),
    [trend]
  );

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <h2 style={{ margin: 0 }}>Salary Disbursement Dashboard</h2>
        <span style={{ marginLeft: "auto", color: "#64748b", fontSize: 12 }}>
          {payPeriodId ? `Pay period ${payPeriodId}` : "All periods"}
        </span>
      </div>

      {error && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            background: "#fee2e2",
            color: "#991b1b",
            borderRadius: 6,
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          marginTop: 16,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
        }}
      >
        <StatCard
          Icon={TrendingUp}
          label="Total salary"
          value={formatMoney(totals.total_amount)}
          tone="blue"
        />
        <StatCard
          Icon={CheckCircle2}
          label="Employees paid"
          value={totals.paid_items ?? 0}
          sublabel={`of ${totals.total_items ?? 0}`}
          tone="green"
        />
        <StatCard
          Icon={Clock}
          label="Pending"
          value={totals.pending_items ?? 0}
          tone="amber"
        />
        <StatCard
          Icon={AlertTriangle}
          label="Failed"
          value={totals.failed_items ?? 0}
          tone="red"
        />
        <StatCard
          Icon={TrendingUp}
          label="Success rate"
          value={`${totals.success_rate_pct ?? 0}%`}
          tone="slate"
        />
      </div>

      <div
        style={{
          marginTop: 20,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
        }}
      >
        <div style={{ background: "#fff", padding: 16, borderRadius: 12, border: "1px solid #e5e7eb" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Landmark size={16} color="#1e40af" />
            <h3 style={{ margin: 0, fontSize: 14 }}>Bank-wise distribution</h3>
          </div>
          <table style={{ width: "100%", marginTop: 10, borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", background: "#f8fafc" }}>
                <th style={{ padding: 6 }}>Bank</th>
                <th style={{ padding: 6 }}>Items</th>
                <th style={{ padding: 6 }}>Amount</th>
                <th style={{ padding: 6 }}>✓</th>
                <th style={{ padding: 6 }}>✗</th>
              </tr>
            </thead>
            <tbody>
              {bankRows.map((b) => (
                <tr key={b.bank_id} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td style={{ padding: 6, fontFamily: "monospace" }}>{b.bank_code}</td>
                  <td style={{ padding: 6 }}>{b.item_count}</td>
                  <td style={{ padding: 6 }}>{formatMoney(b.total_amount)}</td>
                  <td style={{ padding: 6, color: "#166534" }}>{b.succeeded_items}</td>
                  <td style={{ padding: 6, color: "#991b1b" }}>{b.failed_items}</td>
                </tr>
              ))}
              {bankRows.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 10, color: "#64748b" }}>
                    No data yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ background: "#fff", padding: 16, borderRadius: 12, border: "1px solid #e5e7eb" }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>Last 6 months</h3>
          <div style={{ marginTop: 14, display: "flex", gap: 8, alignItems: "flex-end", height: 140 }}>
            {trend.length === 0 ? (
              <div style={{ color: "#64748b", fontSize: 13 }}>No historical data yet.</div>
            ) : (
              trend.map((t) => {
                const pct = Math.max(
                  4,
                  Math.round((Number(t.total_amount) / maxTrendAmount) * 100)
                );
                return (
                  <div
                    key={t.month}
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      height: "100%",
                    }}
                  >
                    <div
                      title={`${t.month} — ${formatMoney(t.total_amount)}`}
                      style={{
                        width: "60%",
                        height: `${pct}%`,
                        background: "#2563eb",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>
                      {t.month?.slice(0, 7)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {loading && <div style={{ color: "#64748b", marginTop: 12 }}>Loading…</div>}
    </div>
  );
}
